import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, X, Play, Settings } from 'lucide-react';
import { ValidationError } from '../types/api';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';

interface RecoverableErrorModalProps {
  botName: string;
  errors: ValidationError[];
  onForceStart: () => void;
  onClose: () => void;
}

const RecoverableErrorModal: React.FC<RecoverableErrorModalProps> = ({
  botName,
  errors,
  onForceStart,
  onClose,
}) => {
  // Get component icon and color based on error type
  const getComponentInfo = (component: string) => {
    switch (component) {
      case 'telegram':
        return { color: 'text-blue-500', bgColor: 'bg-blue-500/10', label: 'Telegram' };
      case 'binance':
        return { color: 'text-yellow-500', bgColor: 'bg-yellow-500/10', label: 'Binance' };
      case 'gemini':
        return { color: 'text-purple-500', bgColor: 'bg-purple-500/10', label: 'Gemini' };
      default:
        return { color: 'text-gray-500', bgColor: 'bg-gray-500/10', label: component };
    }
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Some Services Unavailable
          </DialogTitle>
          <DialogDescription>
            The following issues were detected while starting <strong>{botName}</strong>.
            You can start the bot anyway, but some features may not work.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          {errors.map((error, index) => {
            const info = getComponentInfo(error.component);
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`p-4 rounded-lg border ${info.bgColor} border-${info.color.replace('text-', '')}/20`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-full ${info.bgColor}`}>
                    <AlertTriangle className={`h-4 w-4 ${info.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded ${info.bgColor} ${info.color}`}>
                        {info.label}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {error.code}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-foreground">
                      {error.message}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {error.suggestion}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto"
          >
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={onForceStart}
            className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700"
          >
            <Play className="h-4 w-4 mr-2" />
            Start Anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RecoverableErrorModal;
